import type { E2EScenarioName, IDurableJob, IE2EEstate } from '@condo/domain';
import { openDownloadLink } from '../shared';
import { getClient } from './api-client';

export interface IDebeziumSnapshotSignal {
    id: string;
    type: string;
    data: string;
}

export interface IAdditionalCondition {
    dataCollection: string;
    filter: string;
}

export const runDataMigrations = async (): Promise<IDurableJob> => {
    return getClient('basecampMaintenance')
        .post('/maintenance/data-migrations')
        .then(r => r.data);
};

export const executeUseCase = async (usecaseName: string, usecasePayload: any): Promise<any> => {
    return getClient('basecampMaintenance')
        .post('/maintenance/execute-usecase', {
            usecaseName,
            usecasePayload,
        })
        .then(r => r.data);
};

export const exportAuthzToCSV = async (): Promise<void> => {
    const {
        data: { csv },
    } = await getClient('basecampMaintenance').post('/maintenance/export-authz-to-csv');

    const url = window.URL.createObjectURL(new Blob([csv]));
    openDownloadLink(url, 'authz.csv');
};

export const fetchDatabaseTables = async (schema = 'public'): Promise<string[]> => {
    return getClient('basecampMaintenance')
        .get('/maintenance/database-tables', { params: { schema } })
        .then(r => r.data.tables);
};

export const fetchSnapshotSignals = async (snapshotTriggerTable = 'debezium_signal', limit = 50): Promise<IDebeziumSnapshotSignal[]> => {
    return getClient('basecampMaintenance')
        .get('/maintenance/snapshot-signals', { params: { snapshotTriggerTable, limit } })
        .then(r => r.data.signals);
};

export const createTableSnapshotRequest = async (params: {
    tables: string[];
    snapshotTriggerTable?: string;
    additionalConditions?: IAdditionalCondition[];
}): Promise<void> => {
    return getClient('basecampMaintenance')
        .post('/maintenance/table-snapshot-request', params)
        .then(r => r.data);
};

export const createTableSnapshotStopRequest = async (params: { tables: string[]; snapshotTriggerTable?: string }): Promise<void> => {
    return getClient('basecampMaintenance')
        .post('/maintenance/table-snapshot-stop-request', params)
        .then(r => r.data);
};

/**
 * E2E Estate Seeds API
 */
export const getE2EEstates = async (params?: { page?: number; size?: number }): Promise<{ estates: IE2EEstate[]; total: number }> =>
    getClient('basecamp')
        .get('/e2e/estates', { params })
        .then(response => response.data);

export const getE2EScenarios = async (): Promise<Record<string, E2EScenarioName[]>> =>
    getClient('basecamp')
        .get('/e2e/scenarios')
        .then(response => response.data);

export const postE2EScenarioJob = async (scenarioName: string, body?: { params?: Record<string, any> }): Promise<IDurableJob> =>
    getClient('basecampMaintenance')
        .post(`/maintenance/e2e-scenario/${scenarioName}/job`, body)
        .then(response => response.data);

export const deleteE2EScenarioTeardown = async (scenarioName: string, token: string): Promise<void> =>
    getClient('basecampMaintenance').delete(`/maintenance/e2e-scenario/${scenarioName}`, { params: { token } });
